import hsFetch from 'js/sign-components/common/hs-fetch';
import type { SignerAppRequest, Page, Rule, SignerField } from 'js/sign-components/generated/types/HelloRequest';
import { assertDataIsValid } from 'js/sign-components/common/ajv-validation';

export type LegacySignerData = {
    dpi: Number,
    pages: Page[];
    fields: SignerField[];
    rules: Rule[];
    signedData?: Record<string, {
        type: string;
        data: Record<string, unknown>;
    }>;
};

/**
 * Custom request object sent by the legacy signer application for the
 * '/signer/ready' endpoint
 *
 * **NOTE**: Incomplete type definition. Only types data known to be used by
 * existing stories and tests.
 */
export type LegacySignerReadyRequest = {
  query: {
    // eslint-disable-next-line camelcase
    tsm_guid: string,
    token: string
  }
}

/**
 * Response sent by the server for '/signer/ready' endpoint
 *
 * **NOTE**: Incomplete type definition. Only types data known to be used by
 * existing stories and tests.
 */
export type LegacySignerReadyResponse = {
  success: boolean;
  error: unknown;
  data: {
    ready: boolean;
    error: boolean;
  } | null;
};

/**
 * Custom request object sent by the legacy signer application for the
 * '/signer/load' endpoint
 *
 * **NOTE**: Incomplete type definition. Only types data known to be used by
 * existing stories and tests.
 */
export type LegacySignerLoadRequest = {
  query: {
    guid: string;
    /* eslint-disable camelcase */
    status_token: string;
    access_code?: unknown;
    signature_id?: unknown;
    selected_signer?: unknown;
    tsm_guid?: string;
    trigger_sms?: unknown;
    sms_auth_code?: unknown;
    /* eslint-enable camelcase */
  }
};

/**
 * Response sent by the server for '/signer/load' endpoint
 *
 * **NOTE**: Incomplete type definition. Only types data known to be used by
 * existing stories and tests.
 */
export type LegacySignerLoadResponse = {
  success: boolean;
  error: unknown;
  data: unknown;
};


/**
 * Custom request object sent by the legacy signer application for the
 * '/signer/save' endpoint
 *
 * **NOTE**: Incomplete type definition. Only types data known to be used by
 * existing stories and tests.
 */
export type LegacySignerSaveRequest = {
  data: string,
}

/**
 * Response sent by the server for '/signer/save' endpoint
 *
 * **NOTE**: Incomplete type definition. Only types data known to be used by
 * existing stories and tests.
 */
export type LegacySignerSaveResponse = {
  success: boolean,
  error: unknown | null,
  data: unknown | null
}

/**
 * Custom request object sent by the legacy signer application for the
 * '/signer/finalize' endpoint
 *
 * **NOTE**: Incomplete type definition. Only types data known to be used by
 * existing stories and tests.
 */
export type LegacySignerFinalizeRequest = {
  data: string,
}

/**
 * Response sent by the server for '/signer/finalize' endpoint
 *
 * **NOTE**: Incomplete type definition. Only types data known to be used by
 * existing stories and tests.
 */
export type LegacySignerFinalizeResponse = {
  success: boolean,
  error: unknown,
  data: unknown
}

function mapHelloRequestToLegacy(data: SignerAppRequest): LegacySignerData {
  return {
    dpi: 80,
    ...data,
  };
}

export async function loadData(cachedParamsToken: string): Promise<LegacySignerData> {
  const url = `/signer/getData?cached_params_token=${cachedParamsToken}`;
  const response = await hsFetch(url);
  if (!response.ok) {
    const errorResponse = await response.text();
    let errorMessage = 'unable to fetch signer data';
    if (errorResponse) {
      errorMessage += ` - ${errorResponse}`;
    }
    throw new Error(errorMessage);
  }
  const { data } = await response.json();
  return {
    dpi: 80,
    ...data,
  };
}


export async function loadUnifiedData(cachedParamsToken: string): Promise<LegacySignerData> {
  const url = `/signer/getUnifiedData?cached_params_token=${cachedParamsToken}`;
  const response = await hsFetch(url);
  if (!response.ok) {
    const errorResponse = await response.text();
    let errorMessage = 'unable to fetch signer data';
    if (errorResponse) {
      errorMessage += ` - ${errorResponse}`;
    }
    throw new Error(errorMessage);
  }
  const { data } = await response.json();
  const { validateSignerAppRequest } = await import('js/sign-components/generated/validators/validateHelloRequest');
  assertDataIsValid(validateSignerAppRequest, data);
  return mapHelloRequestToLegacy(data);
}

/**
 * NOTE: This is only used in stories/tests and not the real application.
 */
export async function ready(
  _params: LegacySignerReadyRequest,
): Promise<LegacySignerReadyResponse> {
  throw new Error('Not implemented');
}

/**
 * NOTE: This is only used in stories/tests and not the real application.
 */
export async function load(
  _params: LegacySignerLoadRequest,
): Promise<LegacySignerLoadResponse> {
  throw new Error('Not implemented');
}

/**
 * NOTE: This is only used in stories/tests and not the real application.
 */
export async function save(
  _params: LegacySignerSaveRequest,
): Promise<LegacySignerSaveResponse> {
  throw new Error('Not implemented');
}

/**
 * NOTE: This is only used in stories/tests and not the real application.
 */
export async function finalize(
  _params: LegacySignerFinalizeRequest,
): Promise<LegacySignerFinalizeResponse> {
  throw new Error('Not implemented');
}
